import React, { useState, useRef, useEffect } from 'react';

interface Doctor {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface RescheduleDoctorSelectProps {
  doctors: Doctor[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  placeholder?: string;
  t?: any;
}

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#361777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

export const RescheduleDoctorSelect: React.FC<RescheduleDoctorSelectProps> = ({ doctors, selectedId, onSelect, placeholder, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedDoctor = doctors.find(d => d.id === selectedId);

  return (
    <div className="resch-doc-select-container" ref={containerRef}>
      <div 
        className={`resch-doc-select-trigger ${isOpen ? 'open' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="resch-doc-select-value">
          {selectedDoctor ? (
            <div className="resch-doc-item">
              <div className="resch-doc-avatar">
                {selectedDoctor.avatarUrl ? (
                  <img src={selectedDoctor.avatarUrl} alt={selectedDoctor.name} />
                ) : (
                  <div className="resch-doc-avatar-placeholder">
                    {selectedDoctor.name.charAt(0)}
                  </div>
                )}
              </div>
              <span className="resch-doc-name">{selectedDoctor.name}</span>
            </div>
          ) : (
            <span className="resch-doc-placeholder">{placeholder || (t ? t('doc_placeholder') : 'Lekarz')}</span>
          )}
        </div>
        <div className="resch-doc-select-icon">
          <ChevronDownIcon />
        </div>
      </div>

      {isOpen && (
        <div className="resch-doc-select-dropdown">
          <div 
            className={`resch-doc-select-option ${selectedId === null ? 'selected' : ''}`}
            onClick={() => {
              onSelect(null);
              setIsOpen(false);
            }}
          >
            <div className="resch-doc-item">
              <div className="resch-doc-avatar">
                <div className="resch-doc-avatar-any"></div>
              </div>
              <span className="resch-doc-name">{t ? t('any_doctor') : 'Dowolny lekarz'}</span>
            </div>
          </div>
          {doctors.map(doc => (
            <div 
              key={doc.id}
              className={`resch-doc-select-option ${selectedId === doc.id ? 'selected' : ''}`}
              onClick={() => {
                onSelect(doc.id);
                setIsOpen(false);
              }}
            >
              <div className="resch-doc-item">
                <div className="resch-doc-avatar">
                  {doc.avatarUrl ? (
                    <img src={doc.avatarUrl} alt={doc.name} />
                  ) : (
                    <div className="resch-doc-avatar-placeholder">
                      {doc.name.charAt(0)}
                    </div>
                  )}
                </div>
                <span className="resch-doc-name">{doc.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .resch-doc-select-container {
          position: relative;
          width: 100%;
          font-family: 'Poppins', sans-serif;
        }
        .resch-doc-select {
          position: relative;
          width: 100%;
        }
        .resch-doc-select-trigger {
          width: 100%;
          border-radius: 24px;
          background: #FFFFFF;
          border: 1px solid #57437E;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          min-height: 56px;
          transition: all 0.2s ease;
        }
        .resch-doc-select-trigger:hover, .resch-doc-select-trigger.open {
          border-color: #361777;
        }
        .resch-doc-select-value {
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: #361777;
          font-weight: 500;
        }
        .resch-doc-placeholder {
          color: #797B86;
          font-size: 15px;
        }
        .resch-doc-select-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease;
        }
        .resch-doc-select-trigger.open .resch-doc-select-icon {
          transform: rotate(180deg);
        }
        .resch-doc-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .resch-doc-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
        }
        .resch-doc-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .resch-doc-avatar-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #361777;
          color: #fff;
          font-weight: 600;
          font-size: 14px;
        }
        .resch-doc-avatar-any {
          width: 100%;
          height: 100%;
          background: #EFE8F9;
        }
        .resch-doc-name {
          font-size: 15px;
          color: #361777;
          font-weight: 500;
        }
        
        .resch-doc-select-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #57437E;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.06);
          z-index: 50;
          overflow: hidden;
          padding: 8px 0;
        }
        .resch-doc-select-option {
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.1s ease;
        }
        .resch-doc-select-option:hover {
          background: #F8F6FC;
        }
        .resch-doc-select-option.selected {
          background: #F8F6FC;
        }
      `}</style>
    </div>
  );
};
